import React, { useState, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Tab, Tabs,
  Alert, Avatar, LinearProgress, useTheme, useMediaQuery,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
  Chip, Stack, TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, InputAdornment, Paper, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsCar as CarIcon,
  AccessTime as TimeIcon,
  CheckCircle,
  Business as DepartmentIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Public as PublicIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import EnhancedRequestForm from '../../components/EnhancedRequestForm';
import ApprovalDashboard from '../../components/ApprovalDashboard';
import VehicleCalendarScheduler from '../../components/VehicleCalendarScheduler';
import { SupervisorApprovalDialog, FleetApprovalDialog, RejectionDialog } from '../../components/ApprovalDialogs';
import { useAuth } from '../../contexts/AuthContext';
import {
  QK,
  useAllRequests, usePendingSupervisor, usePendingFleet,
  useRequestStats, useAllVehicles,
  useSupervisorApprove, useSupervisorReject,
  useFleetApprove, useFleetReject, useSubmitRequest,
  useCompleteTrip, useCancelTrip
} from '../../hooks/useFleetData';
import { vehicleRequestAPI } from '../../services/api';
import type { VehicleRequest, FleetVehicle } from '../../types';
import { formatDate, formatTime, formatShort } from '../../utils/dateUtils';

// ── Helpers ───────────────────────────────────────────────────────────────────
const getRequesterDisplay = (r: any): string => {
  if (r?.isPublicRequest && r?.requesterName) return r.requesterName;
  const rb = r?.requestedBy;
  if (rb && typeof rb === 'object' && rb.firstName) return `${rb.firstName} ${rb.lastName || ''}`.trim();
  return 'Unknown';
};

const statusColor = (s: string): any => {
  const map: Record<string, any> = {
    pending: 'warning', supervisor_approved: 'info', fleet_approved: 'success',
    in_progress: 'primary', completed: 'success', rejected: 'error', cancelled: 'default',
  };
  return map[s] || 'default';
};

const statusLabel = (s: string): string => {
  const map: Record<string, string> = {
    pending: 'Pending', supervisor_approved: 'Pending Fleet', fleet_approved: 'Approved',
    in_progress: 'In Progress', completed: 'Completed', rejected: 'Rejected', cancelled: 'Cancelled',
  };
  return map[s] || s;
};

type StatusFilter = 'all' | 'pending' | 'supervisor_approved' | 'fleet_approved' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
type SourceFilter = 'all' | 'portal' | 'internal';

// ── History Tab ───────────────────────────────────────────────────────────────
const RequestHistoryTab: React.FC<{
  requests: VehicleRequest[];
  loading: boolean;
  onRefresh: () => void;
  currentUserId?: string;
  currentUserRole?: string;
}> = ({ requests, loading, onRefresh, currentUserId, currentUserRole }) => {
  const { company } = useAuth();                            
  const tz = company?.settings?.timezone || 'Africa/Lagos';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRPP] = useState(15);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const completeTrip = useCompleteTrip();
  const cancelTrip = useCancelTrip();

  const handleComplete = async (requestId: string) => {
    setActionLoading(requestId);
    try {
      await completeTrip.mutateAsync({ requestId, completionData: {} });
      onRefresh();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to complete trip');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (requestId: string) => {
    if (!window.confirm('Cancel this request?')) return;
    setActionLoading(requestId);
    try {
      await cancelTrip.mutateAsync({ requestId, reason: 'Cancelled by requester' });
      onRefresh();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => requests.filter((r: any) => {
    const q = search.toLowerCase();
    const hit = !search ||
      getRequesterDisplay(r).toLowerCase().includes(q) ||
      r.purpose?.toLowerCase().includes(q) ||
      r.destination?.toLowerCase().includes(q) ||
      r.referenceNumber?.toLowerCase().includes(q);
    const st = status === 'all' || r.status === status;
    const src = source === 'all' || (source === 'portal' ? r.isPublicRequest : !r.isPublicRequest);
    return hit && st && src;
  }), [requests, search, status, source]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasFilters = search || status !== 'all' || source !== 'all';

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="flex-start" flexWrap="wrap">
        <TextField size="small" placeholder="Search purpose, destination, requester, ref..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={e => { setStatus(e.target.value as StatusFilter); setPage(0); }}>
            {['all', 'pending', 'supervisor_approved', 'fleet_approved', 'in_progress', 'completed', 'rejected', 'cancelled']
              .map(s => <MenuItem key={s} value={s}>{s === 'all' ? 'All Statuses' : statusLabel(s)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Source</InputLabel>
          <Select value={source} label="Source" onChange={e => { setSource(e.target.value as SourceFilter); setPage(0); }}>
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="portal">Portal</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
          </Select>
        </FormControl>
        {hasFilters && <Button size="small" startIcon={<ClearIcon />} onClick={() => { setSearch(''); setStatus('all'); setSource('all'); setPage(0); }} color="inherit">Clear</Button>}
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading} sx={{ ml: 'auto' }}>Refresh</Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filtered.length} result{filtered.length !== 1 ? 's' : ''}{hasFilters ? ' (filtered)' : ''}
      </Typography>

      {paged.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <FilterIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">{hasFilters ? 'No requests match your filters' : 'No requests yet'}</Typography>
        </Paper>
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['Requester', 'Purpose', 'Destination', 'Trip Date', 'Priority', 'Status', 'Source', 'Submitted', 'Actions']
                  .map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((r: any) => (
                <TableRow key={r._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{getRequesterDisplay(r)}</Typography>
                    {(r.requesterDepartment || r.requestedBy?.department) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {r.requesterDepartment || r.requestedBy?.department}
                      </Typography>
                    )}
                    {r.referenceNumber && (
                      <Typography variant="caption" color="primary" sx={{ fontFamily: 'monospace' }} display="block">
                        {r.referenceNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{r.purpose}</Typography></TableCell>
                  <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>{r.destination}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(r.startDateTime, tz)}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatTime(r.startDateTime, tz)}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" color={statusColor(r.priority === 'urgent' ? 'error' : r.priority)} label={r.priority} variant="outlined" /></TableCell>
                  <TableCell><Chip size="small" color={statusColor(r.status)} label={statusLabel(r.status)} /></TableCell>
                  <TableCell>
                    {r.isPublicRequest
                      ? <Chip size="small" icon={<PublicIcon />} label="Portal" variant="outlined" color="secondary" />
                      : <Chip size="small" label="Internal" variant="outlined" />}
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatShort(r.createdAt, tz)}</Typography></TableCell>

                  <TableCell>
                    {(() => {
                      const isRequester = r.requestedBy?._id === currentUserId ||
                        r.requestedBy === currentUserId;
                      const isAdmin = ['company_admin', 'fleet_manager'].includes(currentUserRole || '');
                      const isLoading = actionLoading === r._id;

                      // Show Complete button for fleet_approved or in_progress
                      if (['fleet_approved', 'in_progress'].includes(r.status) && (isRequester || isAdmin)) {
                        return (
                          <Button size="small" variant="contained" color="success"
                            disabled={isLoading}
                            onClick={() => handleComplete(r._id)}
                            sx={{ fontSize: '0.7rem', textTransform: 'none', fontWeight: 700, minWidth: 80 }}>
                            {isLoading ? '...' : 'Complete'}
                          </Button>
                        );
                      }

                      // Show Cancel button for pending/supervisor_approved/fleet_approved
                      if (['pending', 'supervisor_approved', 'fleet_approved'].includes(r.status) && (isRequester || isAdmin)) {
                        return (
                          <Button size="small" variant="outlined" color="error"
                            disabled={isLoading}
                            onClick={() => handleCancel(r._id)}
                            sx={{ fontSize: '0.7rem', textTransform: 'none', fontWeight: 700, minWidth: 80 }}>
                            {isLoading ? '...' : 'Cancel'}
                          </Button>
                        );
                      }

                      return null;
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRPP(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </Card>
      )}
    </Box>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
interface RequestFormData {
  purpose: string; destination: string;
  startDateTime: Date; endDateTime: Date;
  passengerCount: number; vehicleCategory: string;
  specialRequirements: string; priority: 'low' | 'medium' | 'high' | 'urgent';
}

const VehicleRequestsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState(0);
  const [openNewRequest, setOpenNewRequest] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  // Dialog states
  const [supervisorDialog, setSupervisorDialog] = useState<{ open: boolean; requestId: string | null; requestDetails: any }>({ open: false, requestId: null, requestDetails: null });
  const [fleetDialog, setFleetDialog] = useState<{ open: boolean; requestId: string | null; requestDetails: any; availableVehicles: FleetVehicle[] }>({ open: false, requestId: null, requestDetails: null, availableVehicles: [] });
  const [rejectionDialog, setRejectionDialog] = useState<{ open: boolean; requestId: string | null; requestDetails: any; rejectionType: 'supervisor' | 'fleet' }>({ open: false, requestId: null, requestDetails: null, rejectionType: 'supervisor' });
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  const { user, hasPermission, company } = useAuth();
  const canApprove = hasPermission('write:devices') || user?.role === 'fleet_manager' || user?.role === 'company_admin';

  // ── React Query data ───────────────────────────────────────────────────────
  const { data: allRequestsData, isLoading: requestsLoading, refetch: refetchAll }
    = useAllRequests();
  const requests = allRequestsData || [];

  const { data: pendingSuper = [], refetch: refetchSuper }
    = usePendingSupervisor(canApprove);
  const { data: pendingFleet = [], refetch: refetchFleet }
    = usePendingFleet(canApprove);
  const pendingAll = [...pendingSuper, ...pendingFleet];

  const { data: stats }
    = useRequestStats('30d');
  const { data: availableVehiclesData }
    = useAllVehicles({ status: 'available' });

  // ── Mutations ──────────────────────────────────────────────────────────────
  const supervisorApprove = useSupervisorApprove();
  const supervisorReject = useSupervisorReject();
  const fleetApprove = useFleetApprove();
  const fleetReject = useFleetReject();
  const submitRequest = useSubmitRequest();

  const isAnyMutating = supervisorApprove.isPending || supervisorReject.isPending ||
    fleetApprove.isPending || fleetReject.isPending || submitRequest.isPending;

  const toast = (msg: string, sev: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, msg, sev });

  const refreshAll = () => {
    refetchAll();
    if (canApprove) { refetchSuper(); refetchFleet(); }
  };

  // Tab index helper
  const tabIndex = (name: 'approvals' | 'history' | 'calendar') => {
    if (canApprove) return { approvals: 0, history: 1, calendar: 2 }[name];
    return { approvals: -1, history: 0, calendar: 1 }[name];
  };

  // ── Find request by ID from either list ───────────────────────────────────
  const findRequest = (id: string): any =>
    [...requests, ...pendingAll].find((r: any) => r._id === id);

  // ── Approval handlers ─────────────────────────────────────────────────────
  const handleOpenSupervisorApproval = (requestId: string) => {
    const r = findRequest(requestId);
    if (!r) return;
    setSupervisorDialog({ open: true, requestId, requestDetails: { purpose: r.purpose, destination: r.destination, requester: getRequesterDisplay(r), passengerCount: r.passengerCount } });
  };

  const handleSupervisorApprove = async (comments: string) => {
    if (!supervisorDialog.requestId) return;
    try {
      await supervisorApprove.mutateAsync({ requestId: supervisorDialog.requestId, comments });
      setSupervisorDialog({ open: false, requestId: null, requestDetails: null });
      toast('Request approved!');
    } catch (e: any) { toast(e.response?.data?.message || 'Failed to approve', 'error'); }
  };

  const handleOpenFleetApproval = async (requestId: string) => {
    const r = findRequest(requestId);
    if (!r) return;
    setVehiclesLoading(true);
    try {
      const startDate = typeof r.startDateTime === 'string' ? r.startDateTime : r.startDateTime.toISOString();
      const endDate = typeof r.endDateTime === 'string' ? r.endDateTime : r.endDateTime.toISOString();

      const vehiclesRaw = await vehicleRequestAPI.getAvailableVehicles(startDate, endDate, r.vehicleCategory);
      const availableVehicles: FleetVehicle[] = vehiclesRaw.map((v: any) => ({
        id: v._id || v.id, vin: v.vin, plateNumber: v.plateNumber, category: v.category,
        make: v.make, model: v.model, year: v.year, color: v.color, fuelType: v.fuelType,
        deviceBound: v.deviceBound || false, isActive: v.isActive,
        seatingCapacity: v.seatingCapacity, currentMileage: v.currentMileage,
        createdAt: v.createdAt, updatedAt: v.updatedAt,
      }));

      setFleetDialog({
        open: true, requestId,
        requestDetails: {
          purpose: r.purpose, destination: r.destination, requester: getRequesterDisplay(r),
          passengerCount: r.passengerCount, startDate, endDate, category: r.vehicleCategory,
          requesterPhone: r.requesterPhone || null,
          requesterEmail: r.requesterEmail || null,
          referenceNumber: r.referenceNumber || null,
        },
        availableVehicles,
      });
    } catch { toast('Failed to load available vehicles', 'error'); }
    finally { setVehiclesLoading(false); }
  };

  const handleFleetApprove = async (
    vehicleId: string,
    driverName: string,
    driverPhone: string | null,
    comments: string,
  ) => {
    if (!fleetDialog.requestId) return;
    try {
      await fleetApprove.mutateAsync({
        requestId: fleetDialog.requestId,
        vehicleId,
        driverName,
        driverPhone,
        comments,
      });
      setFleetDialog({ open: false, requestId: null, requestDetails: null, availableVehicles: [] });
      toast('Vehicle assigned successfully!');
    } catch (e: any) { toast(e.response?.data?.message || 'Failed to assign vehicle', 'error'); }
  };

  const handleOpenRejection = (requestId: string, rejectionType: 'supervisor' | 'fleet') => {
    const r = findRequest(requestId);
    if (!r) return;
    setRejectionDialog({ open: true, requestId, requestDetails: { purpose: r.purpose, requester: getRequesterDisplay(r) }, rejectionType });
  };

  const handleReject = async (reason: string) => {
    if (!rejectionDialog.requestId) return;
    try {
      if (rejectionDialog.rejectionType === 'supervisor') {
        await supervisorReject.mutateAsync({ requestId: rejectionDialog.requestId, reason });
      } else {
        await fleetReject.mutateAsync({ requestId: rejectionDialog.requestId, reason });
      }
      setRejectionDialog({ open: false, requestId: null, requestDetails: null, rejectionType: 'supervisor' });
      toast('Request rejected');
    } catch (e: any) { toast(e.response?.data?.message || 'Failed to reject', 'error'); }
  };

  const handleSubmitRequest = async (formData: RequestFormData) => {
    try {
      await submitRequest.mutateAsync({
        purpose: formData.purpose, destination: formData.destination,
        startDateTime: formData.startDateTime.toISOString(),
        endDateTime: formData.endDateTime.toISOString(),
        passengerCount: formData.passengerCount,
        vehicleCategory: formData.vehicleCategory === 'any' ? undefined : formData.vehicleCategory as any,
        specialRequirements: formData.specialRequirements || undefined,
        priority: formData.priority,
      });
      toast('Request submitted!');
    } catch (e: any) { toast(e.response?.data?.message || 'Failed to submit request', 'error'); }
  };

  const loading = requestsLoading || isAnyMutating || vehiclesLoading;

  if (requestsLoading && requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  const displayStats = {
    pending: stats?.pendingRequests ?? 0,
    approvedToday: stats?.approvedRequests ?? 0,
    availableVehicles: availableVehiclesData?.length ?? 0,
    totalRequests: stats?.totalRequests ?? 0,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">Vehicle Requests</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh all data">
            <IconButton onClick={refreshAll} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={!isMobile ? <AddIcon /> : undefined}
            onClick={() => setOpenNewRequest(true)} disabled={loading}
            size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
            {isMobile ? 'New' : 'New Request'}
          </Button>
        </Stack>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Pending', value: displayStats.pending, icon: <TimeIcon />, color: 'warning.main' },
          { label: 'Approved (30d)', value: displayStats.approvedToday, icon: <CheckCircle />, color: 'success.main' },
          { label: 'Available Vehicles', value: displayStats.availableVehicles, icon: <CarIcon />, color: 'primary.main' },
          { label: 'Total (30d)', value: displayStats.totalRequests, icon: <DepartmentIcon />, color: 'info.main' },
        ].map(s => (
          <Grid key={s.label} item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: s.color, width: 40, height: 40 }}>{s.icon}</Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab} onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false} allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {canApprove && (
            <Tab label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>Pending Approvals</span>
                {pendingAll.length > 0 && (
                  <Chip size="small" label={pendingAll.length} color="warning" sx={{ height: 18, fontSize: 10 }} />
                )}
              </Stack>
            } />
          )}
          <Tab label="All Requests" />
          <Tab label={isMobile ? 'Calendar' : 'Calendar & Availability'} />
        </Tabs>

        <CardContent>
          {/* Keep all tabs mounted — CSS show/hide prevents unmount/remount
              so React Query cache is served instantly with zero loading flash */}

          {canApprove && (
            <Box sx={{ display: activeTab === tabIndex('approvals') ? 'block' : 'none' }}>
              <ApprovalDashboard
                requests={pendingAll as VehicleRequest[]}
                onApprove={(id, type) => type === 'supervisor' ? handleOpenSupervisorApproval(id) : handleOpenFleetApproval(id)}
                onReject={(id, type) => handleOpenRejection(id, type)}
                userRole={user?.role as any}
              />
            </Box>
          )}

          <Box sx={{ display: activeTab === tabIndex('history') ? 'block' : 'none' }}>
            <RequestHistoryTab requests={requests as VehicleRequest[]} loading={requestsLoading} onRefresh={refreshAll}
              currentUserId={user?._id}
              currentUserRole={user?.role} />
          </Box>

          <Box sx={{ display: activeTab === tabIndex('calendar') ? 'block' : 'none' }}>
            <VehicleCalendarScheduler />
          </Box>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EnhancedRequestForm open={openNewRequest} onClose={() => setOpenNewRequest(false)} onSubmit={handleSubmitRequest} />

      <SupervisorApprovalDialog
        open={supervisorDialog.open}
        onClose={() => setSupervisorDialog({ open: false, requestId: null, requestDetails: null })}
        onApprove={handleSupervisorApprove}
        requestDetails={supervisorDialog.requestDetails || {}}
        loading={supervisorApprove.isPending}
      />

      <FleetApprovalDialog
        open={fleetDialog.open}
        onClose={() => setFleetDialog({ open: false, requestId: null, requestDetails: null, availableVehicles: [] })}
        onApprove={handleFleetApprove}
        requestDetails={fleetDialog.requestDetails || {}}
        availableVehicles={fleetDialog.availableVehicles}
        loading={fleetApprove.isPending || vehiclesLoading}
      />

      <RejectionDialog
        open={rejectionDialog.open}
        onClose={() => setRejectionDialog({ open: false, requestId: null, requestDetails: null, rejectionType: 'supervisor' })}
        onReject={handleReject}
        requestDetails={rejectionDialog.requestDetails || {}}
        loading={supervisorReject.isPending || fleetReject.isPending}
      />

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.sev} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleRequestsPage;